import { __decorate, __metadata } from 'tslib';
import { Service } from '../../decorators/class/service.js';
import { StateManager } from '../store/state-manager.js';
import { SurfaceService } from '../surface/surface-service.js';
import { BuiltinUiState } from '../store/builtin-state.js';
import { $logger } from '../../controllers/logger/logger.js';

/**
 * 向工作台添加组件工具
 * 移动端将会被替换为MobileWidgetService
 *
 * @public
 */
let WidgetService = class WidgetService {
    constructor(store, surfaceService) {
        this.store = store;
        this.surfaceService = surfaceService;
        this.floatWidgets = new Map();
        this.embedWidgets = new Map();
        this.wrapWidgets = new Map();
        this.insertWidgets = new Map();
        this.multiSurfaceEmbed = new Map();
        this.multiSurfaceWrap = new Map();
        this.excludedWidgets = new Set();
        store.register(BuiltinUiState.FloatWidget, []);
        store.register(BuiltinUiState.EmbedWidget, []);
        store.register(BuiltinUiState.WrapWidget, []);
        store.register(BuiltinUiState.InsertWidget, []);
        store.register(BuiltinUiState.EmbedWidgetContainer, {});
    }
    /**
     * 在注册组件前预先排除部分组件的注册
     * 最好在所有 feature mount 前调用
     * 否则应当使用 remove 方法
     *
     * @param names - 需要排除的组件名称
     * @param type - 需要排除的组件类型
     */
    exclude(names, type) {
        names.forEach((name) => {
            this.excludedWidgets.add(`${type}|${name}`);
        });
    }
    /**
     * 向工作台添加浮动组件
     * 移动端将会被替换为MobileWidgetService
     *
     * @param options - 浮动组件选项
     */
    float(options) {
        if (this.excludedWidgets.has(`float|${options.name}`)) {
            $logger.info(`${options.name} has been excluded from floating widgets`);
            return;
        }
        if (this.floatWidgets.has(options.name)) {
            $logger.error('Conflict float widget name');
        }
        this.floatWidgets.set(options.name, options);
        this.update('float');
    }
    /**
     * 修改组件可视状态，移动端组件出现时，所占空间其余组件将消失
     *
     * @param widgetName - 组件名称
     * @param isVisible - 组件可视状态
     */
    changeFloatWidgetVisbility(widgetName, isVisible) {
        const widget = this.floatWidgets.get(widgetName);
        if (!widget) {
            $logger.error(`cannot find float widget, name: ${widgetName}`);
            return;
        }
        if (widget.visible !== isVisible) {
            widget.visible = isVisible;
            this.update('float');
        }
    }
    /**
     * 查询浮动组件是否可见
     *
     * @param widgetName - 组件名称
     */
    isFloatWidgetVisible(widgetName) {
        var _a, _b;
        return (_b = (_a = this.floatWidgets.get(widgetName)) === null || _a === void 0 ? void 0 : _a.visible) !== null && _b !== void 0 ? _b : false;
    }
    /**
     * 向工作台添加嵌入组件，只会嵌入到当前 surface 中
     *
     * @param options - 嵌入组件选项
     */
    embed(options) {
        var _a, _b, _c;
        if (this.excludedWidgets.has(`embed|${options.name}`)) {
            $logger.info(`${options.name} has been excluded from embed widgets`);
            return;
        }
        const surface = (_a = options.surface) !== null && _a !== void 0 ? _a : (_b = this.surfaceService.info.current) === null || _b === void 0 ? void 0 : _b.id;
        if (surface) {
            this.embedWidgets.set(`${options.name}|${surface}`, Object.assign(Object.assign({}, options), { surface }));
            const surfaces = (_c = this.multiSurfaceEmbed.get(options.name)) !== null && _c !== void 0 ? _c : [];
            surfaces.push(surface);
            this.multiSurfaceEmbed.set(options.name, surfaces);
        }
        this.update('embed');
    }
    /**
     * 设置嵌入组件在指定 surface 中的容器
     *
     * @param container - 嵌入组件容器
     * @param surfaceId - 嵌入组件所在的 surface id
     */
    setEmbedContainer(container, surfaceId) {
        const containerMap = this.store.getValue(BuiltinUiState.EmbedWidgetContainer);
        containerMap[surfaceId] = container;
        this.store.update(BuiltinUiState.EmbedWidgetContainer, Object.assign({}, containerMap));
    }
    /**
     * 向所有 surface 添加一个包装组件
     *
     * @param options - 包装组件选项
     */
    wrap(options) {
        var _a;
        if (this.excludedWidgets.has(`wrap|${options.name}`)) {
            $logger.info(`${options.name} has been excluded from wrap widgets`);
            return;
        }
        if (this.wrapWidgets.has(options.name)) {
            $logger.error('Conflict wrap widget name');
        }
        if (options.surface) {
            this.wrapWidgets.set(`${options.name}|${options.surface}`, options);
            const surfaces = (_a = this.multiSurfaceWrap.get(options.name)) !== null && _a !== void 0 ? _a : [];
            surfaces.push(options.surface);
            this.multiSurfaceWrap.set(options.name, surfaces);
        }
        else {
            this.wrapWidgets.set(options.name, options);
        }
        this.update('wrap');
    }
    insert(options) {
        if (this.excludedWidgets.has(`insert|${options.name}`)) {
            $logger.info(`${options.name} has been excluded from insert widgets`);
            return;
        }
        if (this.insertWidgets.has(options.name)) {
            $logger.error('Conflict insert widget name');
        }
        this.insertWidgets.set(options.name, options);
        this.update('insert');
    }
    /**
     * 移除组件
     *
     * @param name - 组件名称
     * @param type - 组件类型
     */
    remove(name, type) {
        var _a, _b;
        switch (type) {
            case 'embed':
                (_a = this.multiSurfaceEmbed.get(name)) === null || _a === void 0 ? void 0 : _a.forEach((surface) => {
                    this.embedWidgets.delete(`${name}|${surface}`);
                });
                break;
            case 'float':
                this.floatWidgets.delete(name);
                break;
            case 'wrap':
                (_b = this.multiSurfaceWrap.get(name)) === null || _b === void 0 ? void 0 : _b.forEach((surface) => {
                    this.wrapWidgets.delete(`${name}|${surface}`);
                });
                this.wrapWidgets.delete(name);
                break;
            case 'insert':
                this.insertWidgets.delete(name);
                break;
        }
        this.update(type);
    }
    /**
     * 更新某类型组件的渲染
     *
     * @internal
     *
     * @param type - 组件类型
     */
    update(type) {
        switch (type) {
            case 'embed':
                this.store.update(BuiltinUiState.EmbedWidget, [...this.embedWidgets.values()]);
                break;
            case 'float':
                this.store.update(BuiltinUiState.FloatWidget, [...this.floatWidgets.values()]);
                break;
            case 'wrap':
                this.store.update(BuiltinUiState.WrapWidget, [...this.wrapWidgets.values()].sort((a, b) => b.priority - a.priority));
                break;
            case 'insert':
                this.store.update(BuiltinUiState.InsertWidget, [...this.insertWidgets.values()]);
                break;
        }
    }
};
WidgetService = __decorate([
    Service({ overridable: true }),
    __metadata("design:paramtypes", [StateManager, SurfaceService])
], WidgetService);

export { WidgetService };
//# sourceMappingURL=widget-service.js.map
